import { useState } from 'react';
import { Icon } from '../utils/icons';

const TopPagesAndStatus = ({ analyticsData, selectedWidget, widgets, formatNumber }) => {
    const [activeTab, setActiveTab] = useState('pages');


    const statusItems = [
        {
            status: 'active',
            icon: <Icon name="checkCircle" size={16} className="text-green-600" />,
            label: 'Analytics tracking is active',
            description: 'Real-time data collection enabled'
        },
        {
            status: 'info',
            icon: <Icon name="widgets" size={16} className="text-blue-600" />,
            label: `${analyticsData?.summary?.totalWidgets || widgets?.length || 0} widgets total`,
            description: 'Active widget deployments'
        },
        {
            status: 'warning',
            icon: <Icon name="time" size={16} className="text-yellow-600" />,
            label: 'Data appears within minutes',
            description: 'Automatic data processing'
        }
    ];

    const topPages = selectedWidget
        ? analyticsData?.impressionsByPage || []
        : analyticsData?.topPages || [];

    const hasPageData = topPages && topPages.length > 0;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Header with Tabs */}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                    Performance Overview
                </h3>
                <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('pages')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'pages'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Top Pages
                    </button>
                    <button
                        onClick={() => setActiveTab('status')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'status'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        System Status
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="min-h-80">
                {activeTab === 'pages' ? (
                    // Top Pages Tab
                    <div>
                        {hasPageData ? (
                            <div className="overflow-hidden">
                                <div className="space-y-3 overflow-y-auto pr-2" style={{ height: '320px', scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                                    {topPages.slice(0, 10).map((page, index) => {
                                        const isTopPerformer = index === 0;
                                        const viewsOrImpressions = selectedWidget ? page.impressions : page.views;

                                        let displayUrl = '';
                                        let displayTitle = '';
                                        let hostname = '';

                                        try {
                                            const url = new URL(page._id);
                                            displayUrl = url.pathname;
                                            displayTitle = page.pageTitle || url.pathname;
                                            hostname = url.hostname;
                                        } catch {
                                            displayUrl = page._id;
                                            displayTitle = page.pageTitle || page._id;
                                            hostname = 'unknown';
                                        }

                                        return (
                                            <div
                                                key={index}
                                                className={`flex items-center justify-between p-4 rounded-lg border transition-all hover:shadow-md ${isTopPerformer
                                                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-4 min-w-0 flex-1">
                                                    {/* Ranking */}
                                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isTopPerformer
                                                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                                                        : 'bg-gray-200 text-gray-600'
                                                        }`}>
                                                        {index + 1}
                                                    </div>

                                                    {/* Page Info */}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-start space-x-2 mb-1">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-gray-900 truncate pr-2"
                                                                    title={displayTitle}
                                                                    style={{ maxWidth: '200px' }}>
                                                                    {displayTitle.length > 40 ? displayTitle.substring(0, 40) + '...' : displayTitle}
                                                                </div>
                                                            </div>
                                                            {isTopPerformer && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                                                                    🏆 Top
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate pr-2"
                                                            title={page._id}
                                                            style={{ maxWidth: '250px' }}>
                                                            <span className="text-gray-400">{hostname}</span>
                                                            <span className="text-gray-600">{displayUrl.length > 30 ? displayUrl.substring(0, 30) + '...' : displayUrl}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Metrics */}
                                                <div className="flex items-center space-x-4 flex-shrink-0">
                                                    <div className="text-right">
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {formatNumber ? formatNumber(viewsOrImpressions) : viewsOrImpressions?.toLocaleString()}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {selectedWidget ? 'impressions' : 'views'}
                                                        </div>
                                                    </div>
                                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <Icon name="visibility" size={16} className="text-blue-600" />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Summary Stats */}
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div>
                                            <div className="text-lg font-bold text-gray-900">{topPages.length}</div>
                                            <div className="text-xs text-gray-500 font-medium">Total Pages</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-900">
                                                {formatNumber ? formatNumber(topPages.reduce((sum, p) => sum + (selectedWidget ? p.impressions : p.views), 0)) : topPages.reduce((sum, p) => sum + (selectedWidget ? p.impressions : p.views), 0).toLocaleString()}
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium">Total {selectedWidget ? 'Impressions' : 'Views'}</div>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-900">
                                                {topPages.length > 0 ? Math.round((topPages.reduce((sum, p) => sum + (selectedWidget ? p.impressions : p.views), 0) / topPages.length)) : 0}
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium">Avg per Page</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <Icon name="analytics" size={24} className="text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-600 mb-1">
                                    No page data available yet
                                </p>
                                <p className="text-xs text-gray-400">
                                    Page analytics will appear once your widgets receive traffic
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    // Status Tab
                    <div>
                        <div className="space-y-4">
                            {statusItems.map((item, index) => (
                                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border-2 border-gray-200 flex-shrink-0">
                                        {item.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 mb-1">
                                            {item.label}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pro Tip */}
                        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                        <Icon name="help" size={12} className="text-white" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-blue-900 mb-1">
                                        💡 Quick Start Tip
                                    </p>
                                    <p className="text-xs text-blue-800 leading-relaxed">
                                        Embed your widgets on websites to start collecting analytics data.
                                        Data appears within minutes and updates automatically as visitors interact with your widgets.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* System Health */}
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="flex items-center justify-center mb-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-900">System Online</div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-center mb-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-900">Data Processing</div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-center mb-2">
                                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-900">Real-time Updates</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopPagesAndStatus;
